import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { flattenQuery } from 'helpers/flattenQuery';
import type { FolderPath } from 'helpers/folderPaths';
import { FilterConfig } from 'hooks/useListQuery';
import { extractErrorMessage } from 'helpers/extractErrorMessage';

export interface Lock {
  id: number;
  name: string;
  description?: string | null;
  zone_name: string;
  limit: number;
  // Folder support (nap-backend #1076). Zone-scoped.
  folder_id?: number | null;
  folder_path?: FolderPath | null;
  created_at?: string;
  updated_at?: string;
}

interface PaginationState {
  totalItems: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

interface LocksState {
  locks: Lock[];
  pagination: PaginationState;
  allowedZones: string[];
  status: string | null;
  error: string | null;
  config: { header_key: string; label: string; header_type: string }[];
  filtersConfig: FilterConfig[];
  loading: {
    getLocks: boolean;
    getLocksPaginated: boolean;
    createLock: boolean;
    updateLock: boolean;
    deleteLock: boolean;
  };
}

const initialState: LocksState = {
  locks: [],
  pagination: {
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    pageSize: 2000,
  },
  allowedZones: [],
  status: null,
  error: null,
  config: [],
  filtersConfig: [],
  loading: {
    getLocks: false,
    getLocksPaginated: false,
    createLock: false,
    updateLock: false,
    deleteLock: false,
  },
};

// GET /locks (legacy/simple)
export const getLocks = createAsyncThunk<Lock[], void | { zone_name?: string; zone_id?: number }, { rejectValue: string }>(
  'locks/getLocks',
  async (params, { rejectWithValue }) => {
    try {
      const res = await api.get('/locks', {
        params: params ? flattenQuery(params) : undefined,
      });
      return res.data?.locks ?? [];
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.error || err?.message || 'Failed to fetch locks');
    }
  }
);

// GET /locks (paginated)
export const getLocksPaginated = createAsyncThunk('locks/getLocksPaginated', async (params: Record<string, any>) => {
  const res = await api.get('/locks', { params: flattenQuery(params) });
  return res.data;
});

// POST /locks  body: { zone_name, name, limit, description?, limit_allocation? }
export const createLock = createAsyncThunk<
  Lock,
  {
    zone_name: string;
    name: string;
    limit: number;
    description?: string | null;
    limit_allocation?: number;
    folder_id: number | null;
  },
  { rejectValue: string }
>('locks/createLock', async (body, { rejectWithValue }) => {
  try {
    const res = await api.post('/locks', body);
    return res.data as Lock;
  } catch (err: any) {
    return rejectWithValue(err?.response?.data?.error || err?.message || 'Failed to create lock');
  }
});

export const updateLock = createAsyncThunk<
  Lock,
  {
    original_zone_name: string;
    original_name: string;
    zone_name: string;
    name: string;
    limit: number;
    description?: string | null;
    folder_id: number | null;
  },
  { rejectValue: string }
>('locks/updateLock', async ({ original_zone_name, original_name, ...fields }, { rejectWithValue }) => {
  try {
    const res = await api.put(`/locks/${encodeURIComponent(original_zone_name)}/${encodeURIComponent(original_name)}`, fields);
    return res.data as Lock;
  } catch (err: any) {
    return rejectWithValue(err?.response?.data?.error || err?.message || 'Failed to update lock');
  }
});

export const deleteLock = createAsyncThunk<{ zone_name: string; name: string }, { zone_name: string; name: string }, { rejectValue: string }>(
  'locks/deleteLock',
  async ({ zone_name, name }, { rejectWithValue }) => {
    try {
      await api.delete(`/locks/${encodeURIComponent(zone_name)}/${encodeURIComponent(name)}`);
      return { zone_name, name };
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.error || err?.message || 'Failed to delete lock');
    }
  }
);

const locksSlice = createSlice({
  name: 'locks',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // getLocks
      .addCase(getLocks.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.loading.getLocks = true;
      })
      .addCase(getLocks.fulfilled, (state, action) => {
        state.locks = action.payload;
        state.status = 'succeeded';
        state.loading.getLocks = false;
      })
      .addCase(getLocks.rejected, (state, action) => {
        state.status = 'failed';
        state.loading.getLocks = false;
        state.error = extractErrorMessage(action.payload, action.error.message || 'Failed to fetch locks');
      });

    builder
      .addCase(getLocksPaginated.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.loading.getLocksPaginated = true;
      })
      .addCase(getLocksPaginated.fulfilled, (state, action) => {
        state.status = 'succeeded';

        const { locks, config, filtersConfig, pagination, allowedZones } = action.payload;

        state.locks = locks;
        state.config = config;
        state.filtersConfig = filtersConfig || [];
        state.allowedZones = allowedZones || [];

        state.pagination.totalItems = pagination?.total_records ?? 0;
        state.pagination.totalPages = pagination?.total_pages ?? 0;
        state.pagination.pageSize = pagination?.limit ?? state.pagination.pageSize;
        state.pagination.currentPage = (pagination?.current_page ?? 1) > (pagination?.total_pages ?? 0) ? 1 : (pagination?.current_page ?? 1);

        state.loading.getLocksPaginated = false;
      })
      .addCase(getLocksPaginated.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch locks';
        state.loading.getLocksPaginated = false;
      });

    builder
      // createLock
      .addCase(createLock.pending, (state) => {
        state.loading.createLock = true;
        state.error = null;
      })
      .addCase(createLock.fulfilled, (state, action) => {
        state.locks.push(action.payload);
        state.status = 'succeeded';
        state.loading.createLock = false;
        const toastId = 'data-created-lock';
        if (!toast.isActive(toastId)) {
          toast.success(`Lock created successfully!`, { toastId });
        }
      })
      .addCase(createLock.rejected, (state, action) => {
        state.loading.createLock = false;
        state.status = 'failed';
        state.error = extractErrorMessage(action.payload, 'Failed to create lock');
        const toastId = 'data-failed-to-create-lock';
        if (!toast.isActive(toastId)) {
          toast.error(`Failed to create lock! ${state.error}`, { toastId });
        }
      })

      // updateLock
      .addCase(updateLock.pending, (state) => {
        state.loading.updateLock = true;
        state.error = null;
      })
      .addCase(updateLock.fulfilled, (state, action) => {
        const idx = state.locks.findIndex((l) => l.id === action.payload.id);
        if (idx !== -1) state.locks[idx] = action.payload;
        state.status = 'succeeded';
        state.loading.updateLock = false;
        const toastId = 'data-updated-lock';
        if (!toast.isActive(toastId)) {
          toast.success(`Lock updated successfully!`, { toastId });
        }
      })
      .addCase(updateLock.rejected, (state, action) => {
        state.loading.updateLock = false;
        state.status = 'failed';
        state.error = extractErrorMessage(action.payload, 'Failed to update lock');
        const toastId = 'data-failed-to-update-lock';
        if (!toast.isActive(toastId)) {
          toast.error(`Failed to update the lock. ${state.error}`, { toastId });
        }
      })

      // deleteLock
      .addCase(deleteLock.pending, (state) => {
        state.loading.deleteLock = true;
        state.error = null;
      })
      .addCase(deleteLock.fulfilled, (state, action) => {
        const { zone_name, name } = action.payload;
        state.locks = state.locks.filter((l) => !(l.zone_name === zone_name && l.name === name));
        state.status = 'succeeded';
        state.loading.deleteLock = false;
        const toastId = 'data-deleted-lock';
        if (!toast.isActive(toastId)) {
          toast.success(`Lock deleted successfully!`, { toastId });
        }
      })
      .addCase(deleteLock.rejected, (state, action) => {
        state.loading.deleteLock = false;
        state.status = 'failed';
        state.error = extractErrorMessage(action.payload, 'Failed to delete lock');
        const toastId = 'data-failed-to-delete-lock';
        if (!toast.isActive(toastId)) {
          toast.error(`Failed to delete the lock! ${state.error}`, { toastId });
        }
      });
  },
});

export default locksSlice.reducer;
